import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { ERollingOffersInternalViewModel } from '@appcharge/shared-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import useImages from 'api/useImages';
import useOffers from 'api/useOffers';
import usePermissions from 'api/usePermissions';
import useTheme from 'api/useTheme';
import { parseOptionalFreeTilesLayout } from 'common/buyOneGetManyLayout';
import {
  EBadgeType,
  Offer,
  ProgressBarPoint,
  RollingOfferProductSequence
} from 'common/contracts';
import { ERollingOfferFormState, OfferType } from 'constants/enums';
import { BuyOneGetMany } from 'design-system/AcSidebar/constants';
import { useTabTracking } from 'hooks/useTabTracking';
import { errorResponse } from 'utils/errorsTextHelper';
import { OfferFormLayout } from 'views/Offers/components/OfferFormLayout';
import { useOfferDesignOptions } from 'views/Offers/hooks/useOfferDesignOptions';
import { useOfferFormData } from 'views/Offers/hooks/useOfferFormData';
import { useOfferFormSubmit } from 'views/Offers/hooks/useOfferFormSubmit';
import { useProgressBarOffers } from 'views/Offers/hooks/useProgressBarOffers';
import {
  DEFAULT_OFFER_DESIGN_EXTERNAL_ID,
  ROLLING_OFFER_MODEL_BADGE_MAP
} from 'views/Offers/Offer.consts';
import {
  buildBaseOfferPayload,
  buildSchedulePayload
} from 'views/Offers/Offer.utils';
import { OfferDesignTypes } from 'views/OfferDesign/OfferDesign.types';

import FormSectionsTabs from 'components/FormSectionsTabs';
import { Tabs, TabsContent } from 'components/ui/tabs';

import {
  BUY_ONE_GET_MANY_SCHEMA,
  BuyOneGetManyFormFields
} from './BuyOneGetManyForm.consts';
import { IBuyOneGetManyFormProps } from './BuyOneGetManyForm.types';
import {
  buildBuyOneGetManyFormInitialValues,
  buildInitialProductsSequence,
  convertPriceFromCents,
  convertPriceToCents,
  DEFAULT_FREE_TILE_COUNT,
  resolveDesignFreeTileCount
} from './BuyOneGetManyForm.utils';
import { BuyOneGetManyPreview } from './BuyOneGetManyPreview';
import BuyOneGetManyModals from './components/Sections/Modals';
import ConfigurationTab from './components/Tabs/ConfigurationTab';
import ScheduleTab from './components/Tabs/ScheduleTab';
import SettingsTab from './components/Tabs/SettingsTab';

const MIN_PAID = 1;
const MIN_FREE_TILES = 1;
const MIN_ITEMS = MIN_PAID + MIN_FREE_TILES;

const BuyOneGetManyForm: React.FC<IBuyOneGetManyFormProps> = ({
  edit = false,
  dup = false
}): React.ReactElement => {
  const { publisherId, buyOneGetManyId } = useParams();
  const navigate = useNavigate();
  const { canUserEdit } = usePermissions();

  const { addOffer, updateOffer, getOffer } = useOffers({
    offerId: buyOneGetManyId,
    offerType: OfferType.BUY_ONE_GET_MANY
  });

  const { submitOffer } = useOfferFormSubmit({
    edit,
    dup,
    offerLabel: '1+Many',
    addOffer,
    updateOffer,
    formatError: errorResponse
  });

  const [productsSequence, setProductsSequence] = useState<
    RollingOfferProductSequence[]
  >([]);
  const [barPointsValidity, setBarPointsValidity] = useState<
    Record<number, boolean>
  >({});
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [tab, setTab] = useState(ERollingOfferFormState.GENERAL);

  const hasUserTouchedSequenceRef = useRef(false);
  const didApplyDesignSeedRef = useRef(false);

  const handleSequenceChangeFromUser = useCallback<
    React.Dispatch<React.SetStateAction<RollingOfferProductSequence[]>>
  >((value) => {
    hasUserTouchedSequenceRef.current = true;
    setProductsSequence(value);
  }, []);

  const { progressBarOffers } = useProgressBarOffers();

  const {
    offerDesignOptions,
    defaultOfferDesignId,
    currentOfferDesign,
    setCurrentOfferDesign,
    getOfferDesign
  } = useOfferDesignOptions({
    publisherId: publisherId || '',
    offerDesignType: OfferDesignTypes.BuyOneGetMany,
    defaultExternalId:
      DEFAULT_OFFER_DESIGN_EXTERNAL_ID[OfferType.BUY_ONE_GET_MANY],
    offerId: buyOneGetManyId
  });

  const onDataLoaded = useCallback(
    (result: Partial<Offer>): void => {
      setCurrentOfferDesign(result.offerUi?._id || result.offerUi?.offerUiId || '');
      if (result.productsSequence) {
        setProductsSequence(
          (result.productsSequence as RollingOfferProductSequence[]).map(
            (item) => ({
              ...item,
              priceInUsdCents: convertPriceFromCents(item?.priceInUsdCents || 0)
            })
          )
        );
      }
    },
    [setCurrentOfferDesign]
  );

  const { data, isLoading, chosenSegment, setChosenSegment } = useOfferFormData(
    {
      offerId: buyOneGetManyId,
      publisherId,
      getOffer,
      onDataLoaded
    }
  );

  const { getImages } = useImages(publisherId);
  const { getGeneralTheme: generalTheme } = useTheme(publisherId);

  const relevantBadgeType: EBadgeType = useMemo(() => {
    const model = generalTheme.data
      ?.rollingOffersInternalViewModel as ERollingOffersInternalViewModel;
    return ROLLING_OFFER_MODEL_BADGE_MAP[model] ?? EBadgeType.EMBLEM;
  }, [generalTheme.data?.rollingOffersInternalViewModel]);

  const form = useForm<BuyOneGetManyFormFields>({
    resolver: zodResolver(BUY_ONE_GET_MANY_SCHEMA),
    mode: 'onChange',
    defaultValues: buildBuyOneGetManyFormInitialValues({
      data,
      edit,
      dup,
      defaultOfferDesignId,
      getOfferResult: getOffer?.data?.result
    })
  });

  const { formState, watch, handleSubmit, reset } = form;

  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      reset(
        buildBuyOneGetManyFormInitialValues({
          data,
          edit,
          dup,
          defaultOfferDesignId,
          getOfferResult: getOffer?.data?.result
        }),
        { keepDirty: false }
      );
    }
  }, [data, edit, dup, defaultOfferDesignId, getOffer?.data?.result, reset]);

  useEffect(() => {
    if (buyOneGetManyId) return;
    if (productsSequence?.length) return;
    setProductsSequence(
      buildInitialProductsSequence(watch('name') || '', DEFAULT_FREE_TILE_COUNT)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsSequence?.length, buyOneGetManyId]);

  const designFreeTilesLayout = parseOptionalFreeTilesLayout(
    getOfferDesign.data?.onePlusMany?.freeTilesLayout
  );
  useEffect(() => {
    if (buyOneGetManyId) return;
    if (didApplyDesignSeedRef.current) return;
    if (hasUserTouchedSequenceRef.current) return;
    if (designFreeTilesLayout === undefined) return;
    const desired = resolveDesignFreeTileCount(designFreeTilesLayout);
    didApplyDesignSeedRef.current = true;
    if (productsSequence.length === 1 + desired) return;
    setProductsSequence(
      buildInitialProductsSequence(watch('name') || '', desired)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designFreeTilesLayout, buyOneGetManyId]);

  const hasMissingProductDetails = useMemo((): boolean => {
    return productsSequence.some(
      ({ products }) =>
        !products.length ||
        products.some(
          ({ product, quantity }) => !product?.publisherProductId || !quantity
        )
    );
  }, [productsSequence]);

  const watchedOfferUiId = watch('offerUiId');
  useEffect(() => {
    if (!watchedOfferUiId) return;
    setCurrentOfferDesign(watchedOfferUiId);
  }, [watchedOfferUiId, setCurrentOfferDesign]);

  const derivedFreeTilesCount = Math.max(productsSequence.length - 1, 0);

  const buyOneGetManyTabs = useMemo(
    () => [
      { value: ERollingOfferFormState.GENERAL, label: 'Settings' },
      { value: ERollingOfferFormState.CONFIGURATION, label: 'Configuration' },
      { value: ERollingOfferFormState.SCHEDULE, label: 'Schedule' }
    ],
    []
  );

  const handleCustomNavigate = useCallback((value: string): void => {
    setTab(value as ERollingOfferFormState);
  }, []);

  const { handleTabChange } = useTabTracking({
    sidebarTab: BuyOneGetMany.title,
    tabs: buyOneGetManyTabs,
    customNavigate: handleCustomNavigate
  });

  const onSubmit = useCallback(
    (values: BuyOneGetManyFormFields): void => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newOffer: Record<string, any> = {
        ...buildBaseOfferPayload(
          values,
          chosenSegment,
          OfferType.BUY_ONE_GET_MANY
        ),
        priority: values.priority,
        schedule: buildSchedulePayload(values.schedule),
        productsSequence: productsSequence.length
          ? productsSequence.map((seq) => {
              const progressBarPoints: ProgressBarPoint[] | undefined =
                seq.progressBarPoints?.[0]?.barId &&
                seq.progressBarPoints?.[0]?.points
                  ? [
                      {
                        points: seq.progressBarPoints?.[0]?.points,
                        barId: seq.progressBarPoints?.[0]?.barId
                      }
                    ]
                  : [];

              const hasValidProducts = seq.products.some(
                (product) => product?.product?.productId
              );

              let products: unknown[] = [];
              if (hasValidProducts) {
                products = seq.products.map((product) => ({
                  productId: product?.product?.productId,
                  quantity: product.quantity,
                  priority: product.priority,
                  publisherProductId: product.product?.publisherProductId
                }));
              }

              const isFreeSubOffer =
                convertPriceToCents(seq?.priceInUsdCents || 0) === 0;

              return {
                index: seq.index || 1,
                products,
                displayName: seq.displayName || values.name,
                progressBarPoints,
                priceInUsdCents: convertPriceToCents(seq?.priceInUsdCents || 0),
                ...(!isFreeSubOffer &&
                  seq?.priceDiscount?.discount && {
                    priceDiscount: seq?.priceDiscount
                  }),
                ...(!isFreeSubOffer &&
                  seq?.productSale?.sale && {
                    productSale: seq?.productSale
                  }),
                ...(seq?.badges?.length && {
                  badges: [
                    {
                      badgeId: seq.badges[0]?.badge?.badgeId,
                      publisherBadgeId: seq.badges[0]?.badge?.publisherBadgeId
                    }
                  ]
                })
              };
            })
          : []
      };
      submitOffer(newOffer, buyOneGetManyId);
    },
    [chosenSegment, productsSequence, submitOffer, buyOneGetManyId]
  );

  const updateContent = useCallback(async (): Promise<void> => {
    if (!hasMissingProductDetails) {
      await handleSubmit(onSubmit)();
    } else {
      if (edit && watch('active')) {
        setIsUpdateDialogOpen(true);
      } else {
        setIsSaveDialogOpen(true);
      }
    }
  }, [hasMissingProductDetails, handleSubmit, onSubmit, edit, watch]);

  const handleFormSubmit = useCallback((): void => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const formTitle = useMemo((): string => {
    const offerName = getOffer.data?.result?.name;
    if (edit && offerName) {
      return `Edit 1+Many (${offerName})`;
    }
    if (edit) {
      return 'Edit 1+Many';
    }
    return 'Create 1+Many';
  }, [edit, getOffer.data?.result?.name]);

  const areAllBarPointsValid = useMemo((): boolean => {
    return productsSequence.every(
      (_, index) => barPointsValidity[index] !== false
    );
  }, [productsSequence, barPointsValidity]);

  const scheduleValues = watch('schedule');
  const isFormValid = useMemo((): boolean => {
    return (
      !isLoading &&
      formState.isValid &&
      canUserEdit &&
      areAllBarPointsValid &&
      (scheduleValues?.permanent ||
        (scheduleValues?.timeFrames?.length ?? 0) > 0) &&
      productsSequence.length >= MIN_ITEMS &&
      (!edit || formState.isDirty)
    );
  }, [
    isLoading,
    formState.isValid,
    formState.isDirty,
    canUserEdit,
    areAllBarPointsValid,
    scheduleValues?.permanent,
    scheduleValues?.timeFrames?.length,
    productsSequence.length,
    edit
  ]);

  const handleClose = useCallback((): void => {
    navigate('../');
  }, [navigate]);

  const isImagesReady = !getImages.isLoading && !!getImages.data;

  return (
    <OfferFormLayout
      title={formTitle}
      isFormValid={isFormValid}
      onSave={updateContent}
      onClose={handleClose}
      previewContent={
        <BuyOneGetManyPreview
          currentOfferDesign={currentOfferDesign}
          productsSequence={productsSequence}
          progressBarOffers={progressBarOffers}
          freeTilesCount={derivedFreeTilesCount}
        />
      }
      extraContent={
        <BuyOneGetManyModals
          isSaveDialogOpen={isSaveDialogOpen}
          isUpdateDialogOpen={isUpdateDialogOpen}
          setIsSaveDialogOpen={setIsSaveDialogOpen}
          setIsUpdateDialogOpen={setIsUpdateDialogOpen}
          submitForm={handleFormSubmit}
        />
      }
    >
      {isImagesReady && (
        <FormProvider {...form}>
          <Tabs
            className="h-full pt-10 pb-10 w-[528px]"
            value={tab}
            onValueChange={handleTabChange}
          >
            <FormSectionsTabs
              currentTab={tab}
              tabs={[
                {
                  value: ERollingOfferFormState.GENERAL,
                  label: 'Settings',
                  disabled: getOffer.isLoading && edit
                },
                {
                  value: ERollingOfferFormState.CONFIGURATION,
                  label: 'Configuration',
                  disabled: getOffer.isLoading && edit
                },
                {
                  value: ERollingOfferFormState.SCHEDULE,
                  label: 'Schedule',
                  disabled: getOffer.isLoading && edit
                }
              ]}
            />
            <div className="flex-1 flex justify-center">
              <TabsContent
                value={ERollingOfferFormState.GENERAL}
                className="mt-9 w-full"
              >
                <SettingsTab
                  buyOneGetManyId={buyOneGetManyId}
                  edit={edit}
                  offerDesignOptions={offerDesignOptions}
                  chosenSegment={chosenSegment}
                  setChosenSegment={setChosenSegment}
                  data={data}
                  productsSequence={productsSequence}
                  onProductsSequenceChange={handleSequenceChangeFromUser}
                  dup={dup}
                />
              </TabsContent>
              <TabsContent
                value={ERollingOfferFormState.CONFIGURATION}
                className="mt-9 w-full"
              >
                <ConfigurationTab
                  buyOneGetManyId={buyOneGetManyId}
                  productsSequence={productsSequence}
                  onProductsSequenceChange={handleSequenceChangeFromUser}
                  setBarPointsValidity={setBarPointsValidity}
                  relevantBadgeType={relevantBadgeType}
                  progressBarOffers={progressBarOffers}
                />
              </TabsContent>
              <TabsContent
                value={ERollingOfferFormState.SCHEDULE}
                className="mt-9 w-full"
              >
                <ScheduleTab buyOneGetManyId={buyOneGetManyId} edit={edit} />
              </TabsContent>
            </div>
          </Tabs>
        </FormProvider>
      )}
    </OfferFormLayout>
  );
};

export default BuyOneGetManyForm;
